/**
 * ProfilePageModern — native Modern layout for the customer profile.
 * Shares all data + save logic with the Code page via useProfileState().
 */
import { Link } from 'react-router-dom';
import { Home, MapPin, Plus, Star, Trash2, Gift, ChevronRight } from 'lucide-react';
import { Navbar } from '../../components/Navbar';
import { ReferralEmailPanel } from '../../components/ReferralEmailPanel';
import { BackButton } from '../../components/BackButton';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { addressToString, useProfileState } from './ProfilePage';
import { ModernCard, Field, ModernAppearancePanel, ModernAccountInfo, ModernSecurityPanel } from './ProfileModernPanels';

function initials(name, email) {
  const src = (name || email || '?').trim();
  return src.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}

function ProfileHeader({ user }) {
  return (
    <div className="am-card-lg p-5 flex items-center gap-4" data-testid="profile-header">
      <div className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-semibold text-white shrink-0"
        style={{ background: 'var(--am-emerald-600)' }} data-testid="profile-avatar">
        {initials(user?.name, user?.email)}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-lg font-semibold truncate" style={{ color: 'var(--am-ink-1)' }} data-testid="profile-display-name">{user?.name || 'Your profile'}</p>
        <p className="text-sm truncate" style={{ color: 'var(--am-ink-3)' }}>{user?.email}</p>
      </div>
      <Link to="/customer/refer" className="am-chip am-chip-gold shrink-0" data-testid="profile-rewards-link">
        <Gift className="w-3.5 h-3.5" />Rewards<ChevronRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}

function DetailsForm({ p }) {
  return (
    <ModernCard title="Your details" testid="profile-details">
      <form onSubmit={p.handleSubmit} className="space-y-4">
        <Field label="Full name" htmlFor="name">
          <input id="name" name="name" value={p.formData.name} onChange={p.handleChange} className="am-input" placeholder="Your name" data-testid="profile-name" />
        </Field>
        <Field label="Phone" htmlFor="phone">
          <input id="phone" name="phone" value={p.formData.phone} onChange={p.handleChange} className="am-input" placeholder="555-0100" data-testid="profile-phone" />
        </Field>
        <Field label="Birthday" htmlFor="birthday" hint="Unlocks annual bonus points. We only use the month and day.">
          <input id="birthday" name="birthday" type="date" value={p.formData.birthday || ''} onChange={p.handleChange}
            className="am-input" data-testid="profile-birthday" max={new Date().toISOString().slice(0, 10)} />
        </Field>
        <Field label="Delivery address">
          <AddressAutocomplete value={p.formData.address} onChange={p.handleAddressChange} placeholder="Start typing your address…" data-testid="profile-address" />
        </Field>
        <button type="submit" disabled={p.loading} className="am-cta w-full h-12 text-sm" data-testid="save-profile">
          {p.loading ? 'Saving…' : 'Save changes'}
        </button>
      </form>
    </ModernCard>
  );
}

function AddressRow({ addr, onDefault, onDelete }) {
  return (
    <div className="flex items-center justify-between gap-3 p-3 rounded-[10px] border"
      style={{ borderColor: addr.is_default ? 'var(--am-emerald-200)' : 'var(--am-hair)', background: addr.is_default ? 'var(--am-emerald-50)' : 'var(--am-card-alt)' }}
      data-testid={`saved-address-${addr.id}`}>
      <div className="flex items-start gap-2.5 min-w-0 flex-1">
        <Home className="w-4 h-4 mt-0.5 shrink-0" style={{ color: addr.is_default ? 'var(--am-emerald-600)' : 'var(--am-ink-3)' }} />
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold truncate" style={{ color: 'var(--am-ink-1)' }}>{addr.label}</p>
            {addr.is_default && <span className="text-[11px] font-medium" style={{ color: 'var(--am-emerald-700)' }}>Default</span>}
          </div>
          <p className="text-xs truncate" style={{ color: 'var(--am-ink-3)' }}>{addressToString(addr.address)}</p>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {!addr.is_default && (
          <button type="button" onClick={() => onDefault(addr.id)} className="p-2 rounded-full hover:bg-[var(--am-inset)]" title="Set as default" data-testid={`set-default-${addr.id}`}>
            <Star className="w-4 h-4" style={{ color: 'var(--am-ink-3)' }} />
          </button>
        )}
        <button type="button" onClick={() => onDelete(addr.id)} className="p-2 rounded-full hover:bg-red-50" title="Remove address" data-testid={`delete-address-${addr.id}`}>
          <Trash2 className="w-4 h-4 text-red-500" />
        </button>
      </div>
    </div>
  );
}

function AddAddressForm({ p }) {
  const type = p.customLabel ? 'Other' : (p.newLabel || 'Home');
  return (
    <div className="space-y-3 pt-4 mt-4 border-t" style={{ borderColor: 'var(--am-hair)' }} data-testid="add-address-form">
      <p className="am-label">Add an address</p>
      <AddressAutocomplete value={p.newAddress} onChange={p.setNewAddress} placeholder="123 Main St, City, ST…" data-testid="new-address-input" />
      <div className="flex flex-wrap gap-2">
        {['Home', 'Work', 'Other'].map((t) => (
          <button key={t} type="button" onClick={() => p.pickLabelType(t)} data-testid={`new-address-type-${t.toLowerCase()}`}
            className={`am-cat-pill ${type === t ? 'am-active' : ''}`}>{t}</button>
        ))}
      </div>
      {p.customLabel && (
        <input value={p.newLabel} onChange={(e) => p.setNewLabel(e.target.value)} placeholder="Custom label (e.g. Mom's, Gym)"
          className="am-input" maxLength={30} data-testid="new-address-label-custom" />
      )}
      <button type="button" onClick={p.handleAddAddress} disabled={p.addingAddress || !p.newAddressText} className="am-cta w-full h-11 text-sm inline-flex items-center justify-center gap-2" data-testid="add-address-btn">
        <Plus className="w-4 h-4" />{p.addingAddress ? 'Saving…' : 'Save address'}
      </button>
    </div>
  );
}

function SavedAddresses({ p }) {
  return (
    <ModernCard title="Saved addresses" icon={MapPin} testid="saved-addresses"
      aside={<span className="text-xs" style={{ color: 'var(--am-ink-3)' }}>{p.savedAddresses.length} saved</span>}>
      {p.savedAddresses.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--am-ink-3)' }} data-testid="saved-addresses-empty">No saved addresses yet. Add one for one-tap checkout.</p>
      ) : (
        <div className="space-y-2">
          {p.savedAddresses.map((a) => <AddressRow key={a.id} addr={a} onDefault={p.handleSetDefault} onDelete={p.handleDeleteAddress} />)}
        </div>
      )}
      <AddAddressForm p={p} />
    </ModernCard>
  );
}

export function ProfilePageModern() {
  const p = useProfileState();
  return (
    <div className="am-modern-scope min-h-screen" data-testid="profile-page">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-5" data-testid="profile-page-modern">
        <div className="flex items-center gap-3">
          <BackButton to="/customer" />
          <div>
            <p className="am-eyebrow">Your account</p>
            <h1 className="text-3xl font-semibold tracking-tight" data-testid="profile-title">Profile</h1>
          </div>
        </div>
        <ProfileHeader user={p.user} />
        <DetailsForm p={p} />
        {p.showAddresses && <SavedAddresses p={p} />}
        {p.user?.role === 'customer' && <ModernAppearancePanel />}
        {p.user?.role === 'customer' && <ReferralEmailPanel modern />}
        <ModernAccountInfo user={p.user} />
        <ModernSecurityPanel />
      </div>
    </div>
  );
}

export default ProfilePageModern;
